import { slugs } from '@/constants'
import { Media } from '@/utils/EpisodeUtil'
import { episodeToMedia, isASneakPeek } from '@/utils/EpisodeUtil/EpisodeUtil'
import { TypedTrackFn } from '@/utils/analytics'
import {
  getObjectFromLocalStorage,
  GUILD_CONTINUE_WATCHING_COLLECTION,
  writeToLocalStorage,
} from '@/utils/local-storage'
import { cLogger } from '@/utils/logging/client-logger'
import { getWebClient } from '../ApolloClient'
import { Episode, getEpisode } from '../ProjectsService'

export async function saveGuildContinueWatching(episode: Episode, track: TypedTrackFn): Promise<boolean> {
  let media = episodeToMedia(episode)
  try {
    const isSneakPeek = isASneakPeek(episode)
    if (isSneakPeek) {
      if (sneakPeekCompilations.includes(episode.guid)) {
        cLogger().info(
          'A compliation sneak peek was watched. Nothing will be saved to the guild continue watching collection.',
          { episode, media },
        )
        // We currently don't have a good way to handle these, so return true because we can't save and shouldn't try again.
        return true
      }

      const primary = sneakPeekToPrimaryMap[episode.guid]
      if (!primary) {
        cLogger().error(
          'Failed to save a sneak peek for guild continue watching because the required config is missing',
          {
            episode,
            media,
          },
        )
        // even though we didn't save, return true because we can't save and shouldn't try again; this is not an error we can recover from.
        return true
      }

      const response = await getEpisode({ guid: primary.id, projectSlug: primary.slug }, getWebClient())
      const primaryEpisode = response.episode

      if (!primaryEpisode) {
        cLogger().error('Failed to retrieve the primary episode for a sneak peek for guild continue watching', {
          episode,
          media,
          primaryEpisode,
          response,
        })
        return false
      }

      media = episodeToMedia(primaryEpisode, { progress: primary.progress, position: primary.position })
    }

    // The episode object that is passed in or fetched here was retrieved while the user is not a guild member yet.
    // Because the user is not a guild member, the unavailable reason will be set.
    // We are loading this now and saving the entire object instead of fetching it later so that performance on load
    // after the user returns from joining the guild is instant. So we need to remove the unavailable reason here
    // anticipating that user will join the guild and will only see the thumbnail generated by this object after joining the guild.
    // The actual watching of content is unaffected and only logged in guild members can actually watch the content.
    media.unavailableReason = undefined

    const existingCollection = getObjectFromLocalStorage<Media[]>(GUILD_CONTINUE_WATCHING_COLLECTION) ?? []

    // If this episode is already in the array, remove it, then place the new version at the front.
    // This makes it so that whatever has been watched most recently will be at the front of the array
    // while preventing duplicate entries in the event that the same episode is watched more than once.
    const updated = existingCollection.filter((m) => m.guid !== media.guid)
    updated.unshift(media)

    writeToLocalStorage(GUILD_CONTINUE_WATCHING_COLLECTION, updated)

    track('Guild Continue Watching Media Saved', { episode, media })
    cLogger().info(`Successfully saved a media to the guild continue watching collection.`, { episode, media })
    return true
  } catch (err) {
    cLogger().error(
      'An error occurred when attempting to save a media to the guild continue watching collection!',
      {
        episode,
        media,
      },
      err as Error,
    )
    return false
  }
}

export function useGuildContinueWatching(): { medias: Media[] } {
  return { medias: getObjectFromLocalStorage<Media[]>(GUILD_CONTINUE_WATCHING_COLLECTION) ?? [] }
}

// Right now there's not a good way to map a sneak peek episode to a project. Dry Bar has a sneak peek
// that is a compliation of clips from a bunch of guild-gated dry bar specials. So there's not a
// single special that would make sense to link to from the sneak peek. It would probably make the
// most sense to link to /watch/dry-bar-best-of. The continue watching rail is currently set up
// to link to a watchable, so we'll need to think about how to handle this type of scenario.
const sneakPeekCompilations = ['8ad8fe7f-afd5-40fb-b554-14f68eaa63d7']

const sneakPeekToPrimaryMap: Record<
  string,
  { primaryName: string; id: string; slug: string; position: number; progress: number }
> = {
  'b592392e-6273-4db9-8ae9-d301c9bca3b0': {
    primaryName: '23 Blast',
    id: 'c1e6a529-4e70-48af-bf84-076d76c81655',
    slug: slugs.twentyThreeBlast,
    progress: (19.5 / 98.5) * 100,
    position: 1167,
  },
  '2e06432d-6618-4233-b51d-b500f0dea1cc': {
    primaryName: 'After Death',
    id: 'c6fb63e6-6d4a-437e-a79f-72ce45e0a4f3',
    slug: slugs.afterDeath,
    progress: (13.5 / 106) * 100,
    position: 812,
  },
  '7b94bc30-6775-4e6f-913c-aa8a781d5c45': {
    primaryName: 'Cabrini',
    id: '0ee9a941-d947-4000-a884-33c881675057',
    slug: slugs.cabrini,
    progress: (19 / 142) * 100,
    position: 1145,
  },
  'c0b2e364-068f-430a-8a31-5ba2926840e3': {
    primaryName: 'Sight',
    id: 'feba0298-cbde-4bf1-925e-cbc040a4524e',
    slug: slugs.sight,
    progress: (19.5 / 102.75) * 100,
    position: 1162,
  },
  '0c176491-592d-4e98-a88a-08ac1250d98b': {
    primaryName: 'Something to Stand For with Mike Rowe',
    id: '1a068302-aace-40b9-885a-87dd0433f69c',
    slug: slugs.somethingToStandForWithMikeRowe,
    progress: (10.75 / 96.5) * 100,
    position: 644,
  },
  'b1222d10-816f-4666-a104-f4b17f807019': {
    primaryName: 'Sound of Freedom',
    id: '6e5bae7e-c0b5-4003-b3d4-871ca410d8f4',
    slug: slugs.soundOfFreedom,
    progress: (23.25 / 131) * 100,
    position: 1400,
  },
  '13b39227-638b-4ecb-abb9-e7f307f3e695': {
    primaryName: 'Sound of Hope: The Story of Possum Trot',
    id: '1537bef3-6078-4f58-b3a8-d6702e86dc6a',
    slug: slugs.soundOfHopeTheStoryOfPossumTrot,
    progress: (17.25 / 129.5) * 100,
    position: 1025,
  },
  'a5359f77-33fa-4beb-9f26-f47298933fe0': {
    primaryName: 'Surprised by Oxford',
    id: '2d4f325d-89ce-4eb3-a010-ecab49b4cad2',
    slug: slugs.surprisedByOxford,
    progress: (11.5 / 102.5) * 100,
    position: 686,
  },
  '3968ed46-308f-4d3c-92c9-95b15b3832a2': {
    primaryName: 'Sweetwater',
    id: '775eb59b-fa36-4e27-bf5e-318f8c5e9050',
    slug: slugs.sweetwater,
    progress: (14.5 / 118.5) * 100,
    position: 880,
  },
  'f8c8d137-346c-4712-a278-bedfc801a170': {
    primaryName: 'The Shift',
    id: '9d780ca5-3e8e-4d2c-aef6-db623863a3a3',
    slug: slugs.theShift,
    progress: (23.5 / 114) * 100,
    position: 1410,
  },
  '5574275b-3bc9-4682-8453-36ef5d71091a': {
    primaryName: 'The Way',
    id: '32819b71-ea76-4527-a6f8-53098e6fcd3f',
    slug: slugs.theWay,
    progress: (17.75 / 120) * 100,
    position: 1068,
  },
}
